import { html, TemplateResult } from 'lit'
import { connectedShadowParts, loginShadowParts, frameShadowParts } from '../../constants/shadow-parts'
import type { CSSResultGroup } from 'lit'
import { property, query } from 'lit/decorators.js'
import { state } from 'lit/decorators/state.js'
import { PropertyValues } from 'lit'

import '@pushengineering/push-web-components/button'
import '@pushengineering/push-web-components/button-icon'
import '@pushengineering/push-web-components/text'
import '@pushengineering/push-web-components/vertical-stack'
import '@pushengineering/push-web-components/overlay'

import Button from '@pushengineering/push-web-components/button'
import type Overlay from '@pushengineering/push-web-components/overlay'

import fanPassClaimStyles from './fan-pass-claim.styles'
import { ClaimEvents } from '../../events/claim'
import CirkayElement from '../../internal/CirkayElement'
import '../login'
import {
    MintStatus,
    N3xusApiConfig,
    CampaignInformation,
    AccessCheckData,
    ClaimStatus,
} from '../../base/n3xus/types'
import '../login'
import '../frame'
import '../error-screen'
import '../mobile-pass'
import '../connected'
import Login from '../login'
import { ClaimStateTypes, LoginState, CampaignState } from './types'
import { FanPassUser } from '../../controllers/types'
import { LoginController } from '../../controllers/LoginController'
import { MagicOptions } from '../../base/magic/MagicAPI'
import { Environment } from '../../base/n3xus/types'
import { LoginStateTypes } from '../login/types'
import { addSession, getLatestSession, preloadLogos, LogoSet, clearStoredSession, findBoxNode } from '../../utils/helpers'
import { UnlockEvents } from '../../events/unlock'
import { LoginEvents } from '../../events/login.js'
import { N3xusAPIController, ALREADY_MINTED_ERROR } from '../../controllers/N3xusAPIController.js'

/**
 * A web component for Fan Pass claim.
 *
 * @fires fp–claim-claiming - Dispatched when the claim flow starts.
 * @fires fp–claim-claimed - Dispatched when the claim is finished.
 */

/**
 * The `FanPassClaim` class is a custom web component that handles the claiming process of a Fan Pass.
 * It extends the `CirkayElement` and provides various properties and methods to manage the claim state,
 * user authentication, and interaction with the Nexus API.
 *
 * @extends {CirkayElement}
 *
 * @property {String} apiKey - The API key used for authentication with the Nexus API.
 * @property {String} callerId - A unique identifier for the claim process.
 * @property {Object} logoSet - An object containing URLs for different sizes of the logo.
 * @property {String} fanPassTitle - The title of the Fan Pass.
 * @property {String} redeemCode - The code used for redeeming the Fan Pass.
 * @property {Boolean} modal - Indicates if the component should be displayed as a modal.
 * @property {Boolean} validateAfterModal - Indicates if validation should occur after the modal is displayed.
 * @property {Boolean} staging - Indicates if the component is in staging mode.
 * @property {Boolean} debugLogging - Enables or disables debug logging.
 * @property {String} successUrl - The URL to redirect to upon successful claim.
 * @property {Boolean} autoRedirect - Indicates if the user should be automatically redirected upon successful claim.
 * @property {Boolean} showRedeemInput - Indicates if the redeem input field should be shown.
 * @property {Boolean} success - Indicates if the claim was successful.
 * @property {String} error - Stores any error messages encountered during the claim process.
 *
 * @state {ClaimStateTypes} _claimState - The current state of the claim process.
 * @state {FanPassUser} _user - The current user information.
 * @state {LoginState} _loginState - The current login state.
 * @state {StatusData | null} _codeStatus - The current status of the code.
 *
 * @method connectedCallback - Lifecycle method called when the component is added to the DOM.
 * @method update - Lifecycle method called when the component's properties change.
 * @method setUpdateState - Updates the claim state based on the current login status and code status.
 * @method redirect - Redirects the user to the URL specified in the `successUrl` property.
 * @method setCodeStatus - Sets the code status by checking its validity and updates the claim state accordingly.
 * @method validateMint - Validates the minting process by periodically checking the code status.
 * @method handleClaim - Handles the claim process for a Fan Pass.
 * @method logout - Logs out the current user.
 * @method logging - Logs a message to the console if debug logging is enabled.
 * @method renderClaimSuccess - Renders the success message and buttons after a Fan Pass has been successfully claimed.
 * @method renderContent - Renders the content based on the current claim state.
 * @method renderFrame - Renders a frame component with the provided inner content.
 * @method render - Renders the component's template.
 */
//@customElement('fan-pass-claim')
export default class FanPassClaim extends CirkayElement {
    static styles: CSSResultGroup = [fanPassClaimStyles]

    private loginController: LoginController | null = null
    private n3xusApiConfig: N3xusApiConfig | undefined = undefined
    private n3xusAPIController: N3xusAPIController | null = null
    private mintRequestDid: string | null = null
    private walletCheckResponse: AccessCheckData | null | undefined = null
    private loginFlow: boolean = false
    private unlockedContent: boolean = false
    private callerId: string = `fan-pass-claim-${Math.random().toString(36).substring(2, 9)}`
    private interval: any = null

    private checkCallerIdMatch(callerId: string): boolean {
        const expectedSuffix = `${this.apiKey}-${this.campaignId}`;
        return callerId.endsWith(expectedSuffix);
    }

    @property({ type: String }) apiKey = ''

    @property({ type: Object }) logoSet: LogoSet = {
        large: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        medium: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        small: {
            src: 'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp',
        },
        alt: 'Cirkay Logo',
    }

    @property({ type: String }) logoUrl = 'http://cirkay.com'

    @property({ type: String }) fanPassTitle = 'Fan Pass'

    @property({ type: String }) redeemCode = ''

    @property({ type: Boolean }) availibleCampaign = false

    @property({ type: Number }) campaignId: number | null = null

    @property({ type: Boolean }) modal = true

    @property({ type: Boolean }) validateAfterModal = false

    @property({ type: String }) environment: Environment = Environment.PRODUCTION

    @property({ type: Boolean }) debugLogging = false

    @property({ type: String }) successUrl = ''

    @property({ type: String }) campaignUrl = window.location.href

    @property({ type: Boolean }) autoRedirect = false

    @property({ type: Boolean }) showRedeemInput = false

    @property({ type: Boolean }) success = false

    @property({ type: String }) error: string = ''

    @property({ type: String }) claimButtonText: string = 'Claim'

    @property({ type: Boolean }) hideClaimButton = false

    @property({ type: Boolean }) hideConnected = false

    @property({ type: String }) claimIntroText: string = 'Claim your ${fanPassTitle}'

    @property({ type: String }) claimingText: string = 'Claiming - just a moment'

    @property({ type: String }) claimSuccessText: string = 'You\'ve claimed your ${fanPassTitle}!'

    @property({ type: String }) claimSuccessPerkButtonText: string = 'Check out your perks!'

    /** Url for custom faq site  */
    @property({ type: String })
    faqUrl: string = 'https://cirkay.com/faq/'

    @state()
    _claimState: ClaimStateTypes = ClaimStateTypes.validating

    @state()
    _campaignState: CampaignState = CampaignState.available

    /** The page number for pager dots */
    @state()
    _pageNumber: number = 1

    @state()
    _user: FanPassUser | undefined = undefined

    @state()
    _loginState: LoginState | undefined = LoginState.initialising

    @state()
    _codeStatus: ClaimStatus | null | undefined = null

    @query('fp-login')
    _loginComponent: Login | undefined

    @state()
    _alreadyClaimed: boolean = false

    /**
     * Constructs an instance of the FanPassClaim component.
     */
    constructor() {
        super()
        const imgApple = new Image()
        imgApple.src =
            'https://cdn.n3xus.services/production/assets/images/US-UK_Add_to_Apple_Wallet_RGB_101421.svg'

        const imgGoogle = new Image()
        imgGoogle.src =
            'https://cdn.n3xus.services/production/assets/images/enGB_add_to_google_wallet_wallet-button.svg'

        const imgCirkay = new Image()
        imgCirkay.src =
            'https://n3xus-store.s3.eu-west-2.amazonaws.com/staging/assets/cirkay-logo-transparent-no-padding.webp'
    }

    /**
     * Lifecycle method called when the component is added to the DOM.
     * Initializes the login controller and Nexus API, retrieves the redeem code
     * from the URL parameters or component property, and sets the code status.
     * If no redeem code is provided, sets the error state and emits an initialization event.
     *
     * @override
     */
    public connectedCallback() {
        super.connectedCallback()
        this.emit(LoginEvents.Initialising, {
            bubbles: true,
            composed: true,
        })
        try {
            preloadLogos(this.logoSet)
        } catch (error) {
            console.warn('Error loading logo images', error)
        }

        this._claimState = ClaimStateTypes.validating
        const magicApiOptions: MagicOptions = {
            environment: this.environment,
        }
        this.loginController = LoginController.getInstance(
            magicApiOptions,
            this.debugLogging
        )
        this.loginController.addHost(this)

        this.n3xusAPIController = N3xusAPIController.getInstance(this.debugLogging)
        this.n3xusAPIController.addHost(this)

        this.n3xusApiConfig = {
            apiKey: this.apiKey,
            environment: this.environment,
        }
        this.callerId = `${this.callerId}-${this.apiKey}-${this.campaignId}`
        //TODO: This should move to the N3xus controller
        const session = getLatestSession(this.apiKey)
        const minutesAgo = Date.now() - 3.5 * 60 * 1000
        if (
            session &&
            this.loginController.loginInit &&
            this.loginController.loggedIn &&
            !(session.issueTS < minutesAgo)
        ) {
            this._claimState = ClaimStateTypes.redeemedCheckingSession
        }
        this.setUpdateState()
    }

    private async handleMintType() {
        this.logging('handleMintType')
        this.logging('Campaing ID', this.campaignId)
        //If has a promo ID then its not a code redeem its a free to claim with Promo limit or time limits
        if (this.campaignId) {
            const campaign = await this.getCampaignInformation(this.campaignId)
            this.logging('CAMPAIGN details', campaign)
            const currentDate = new Date()
            const startDate: Date | null = campaign.start_date
                ? new Date(campaign.start_date)
                : null
            const endDate: Date | null = campaign.end_date
                ? new Date(campaign.end_date)
                : null
            if (campaign.limit <= campaign.used_count) {
                this._campaignState = CampaignState.noFanPassesLeft
                this.error = `${this.fanPassTitle} can no longer be claimed`
                this._claimState = ClaimStateTypes.error
                this._codeStatus = {
                    status: MintStatus.unavailable,
                    available: campaign.limit - campaign.used_count,
                    campaign: campaign,
                }
            } else if (endDate && currentDate > endDate) {
                this._campaignState = CampaignState.campaignOver
                this.error = 'This claim is no longer available'
                this._claimState = ClaimStateTypes.error
                this._codeStatus = {
                    status: MintStatus.unavailable,
                    available: campaign.limit - campaign.used_count,
                    campaign: campaign,
                }
            } else if (startDate && currentDate < startDate) {
                const timeDiff = startDate.getTime() - currentDate.getTime()

                // Convert to hours
                const totalHours = Math.floor(timeDiff / (1000 * 60 * 60))

                // Calculate days and remaining hours
                const days = Math.floor(totalHours / 24)
                const remainingHours = totalHours % 24
                let text
                // If more than 24 hours, show days and hours
                if (days > 0) {
                    text = `Access to this claim goes live in ${days} ${days === 1 ? 'day' : 'days'} and ${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'}`
                } else {
                    text = `Access to this claim goes live in ${totalHours} ${totalHours === 1 ? 'hour' : 'hours'}`
                }

                this._campaignState = CampaignState.campaignNotBegun
                this.error = text
                this._claimState = ClaimStateTypes.error
                this._codeStatus = {
                    status: MintStatus.unavailable,
                    available: campaign.limit - campaign.used_count,
                    campaign: campaign,
                }
            } else {
                this._codeStatus = {
                    status: MintStatus.available,
                    available: campaign.limit - campaign.used_count,
                    campaign: campaign,
                }
                this.availibleCampaign = true
                this.campaignUrl = campaign.campaign_url
                //Only set to available if user is logged in else just set _codeStatus
                if (this.loginController?.loginInit) {
                    this._claimState = ClaimStateTypes.availableLoggedIn
                }
            }
        } else {
            const urlParams = new URLSearchParams(window.location.search)
            const urlRedeemCode =
                this.redeemCode.trim() === '' && !this.redeemCode
                    ? urlParams.get('code')
                    : this.redeemCode
            if (!urlRedeemCode) {
                this.setCampaignStatus().then(() => {
                    if (!this.availibleCampaign) {
                        this.error =
                            'Seems like your link is invalid, try again or contact support'
                        this._claimState = ClaimStateTypes.error
                    } else if (this.loginController?.loginInit) {
                        this._claimState = ClaimStateTypes.available
                    } else {
                    }
                    return
                })
            } else {
                this.setCodeStatus(null, urlRedeemCode)
            }
        }
    }

    public getUserDetails(): FanPassUser | undefined | null {
        return this.loginController?.user
    }
    
    public getLoginState(): LoginState | undefined {
        return this._loginState
    }

    public getCStatus(): ClaimStatus | null | undefined {
        return this._codeStatus
    }

    /**
     * Updates the component when properties change.
     *
     * @param changedProperties - The properties that have changed.
     */
    protected willUpdate(changedProperties: PropertyValues): void {
        try {
            if (changedProperties.has('apiKey')) {
                this.n3xusApiConfig = {
                    apiKey: this.apiKey,
                    environment: this.environment,
                }
            }
            if (
                changedProperties.has('campaignId') ||
                changedProperties.has('apiKey')
            ) {
                this.handleMintType()
            }
            const detail = this.loginController?.user
                ? {
                      email: this.loginController.user.email ?? null,
                      did: this.loginController.user.did?.token ?? null,
                      wallet: this.loginController?.user.walletAddress ?? null,
                      isLoggedin: this.loginController.loggedIn,
                  }
                : {
                      isLoggedin: this.loginController?.loggedIn,
                  }                
            if (this._loginState === LoginState.initialising && this.loginController?.loginInit) {
                this._loginState = LoginState.initialised
                this.emit(LoginEvents.Initialised, {
                    bubbles: true,
                    composed: true,
                    detail,
                })
                if (this.loginController?.loggedIn) {
                    this._loginState = LoginState.loggedIn
                } else {
                    this._loginState = LoginState.loggedOut
                }   
            }

            if (this._loginState === LoginState.loggedIn && !this.loginController?.loggedIn) {
                this._loginState = LoginState.loggedOut
                this.emit(LoginEvents.Logout, {
                    bubbles: true,
                    composed: true,
                    detail,
                })
                this.stopPolling()
                clearStoredSession()
            }
            
            if (this._loginState === LoginState.loggedOut && this.loginController?.loggedIn) {
                this._loginState = LoginState.loggedIn
                this.emit(LoginEvents.Login, {
                    bubbles: true,
                    composed: true,
                    detail,
                })
            }
            this.setUpdateState()
        } catch (error) {
            console.error('Error in update', error)
        }
    }

    /**
     * Updates the claim state based on the current login status and code status.
     *
     * The method performs the following checks and updates the `_claimState` accordingly:
     *
     * - If the user is not logged in, the claim state is `validating`, and the code status is `redeemed`,
     *   the claim state is set to `redeemedLoggedout`.
     * - If the user is logged in, the claim state is `validating`, the code status is `redeemed`, and the
     *   user's wallet address matches the code's wallet address, the claim state is set to `redeemedLogin`.
     * - If the user is logged in, the claim state is `validating`, the code status is `redeemed`, and the
     *   user's wallet address does not match the code's wallet address, the claim state is set to `redeemedLoggedInInvalid`.
     * - If the user is logged in, the claim state is `validating`, and the code status is `available`,
     *   the claim state is set to `availableLoggedIn`.
     * - If the user is not logged in, the claim state is `validating`, and the code status is `available`,
     *   the claim state is set to `available`.
     *
     * Additionally, if the claim state is `available` and the user logs in, the `handleClaim` method is called.
     *
     * If the claim state is `redeemedLoggedout` and the user logs in, the claim state is updated to either
     * `redeemedLogin` or `redeemedLoggedInInvalid` based on whether the user's wallet address matches the token's wallet address.
     *
     * @private
     */
    private async setUpdateState(): Promise<void> {
        if (
            this._claimState === ClaimStateTypes.redeemedCheckingSession &&
            this.loginController?.loginInit &&
            this.loginController?.loggedIn
        ) {
            const walletCheckResponse = await this.setSessionToken()
            if (walletCheckResponse && walletCheckResponse.data?.holds) {
                this.setPageNumber(3)
                this._claimState = ClaimStateTypes.redeemedLogin
                this.unlockedContentEmit()
            } else if (
                walletCheckResponse &&
                !walletCheckResponse.data?.holds
            ) {
                this._claimState = ClaimStateTypes.availableLoggedIn
            } else {
                this._claimState = ClaimStateTypes.error
            }
        }
        if (
            this.loginController?.loginInit &&
            this._claimState === ClaimStateTypes.validating &&
            !this.loginController?.loggedIn &&
            this._codeStatus?.status === MintStatus.redeemed
        ) {
            this._claimState = ClaimStateTypes.redeemedLoggedout
        } else if (
            this.loginController?.loginInit &&
            this._codeStatus?.status === MintStatus.redeemed &&
            this._claimState === ClaimStateTypes.validating &&
            this.loginController?.loggedIn &&
            this.loginController?.user?.walletAddress ===
                this._codeStatus?.wallet
        ) {
            this.setSessionToken().then(() => {
                this.setPageNumber(3)
                this._claimState = ClaimStateTypes.redeemedLogin
            })
        } else if (
            this.loginController?.loginInit &&
            this._claimState === ClaimStateTypes.validating &&
            this._codeStatus?.status === MintStatus.redeemed &&
            this.loginController?.loggedIn &&
            this.loginController?.user?.walletAddress !==
                this._codeStatus?.wallet
        ) {
            this._claimState = ClaimStateTypes.redeemedLoggedInInvalid
        } else if (
            this.loginController?.loginInit &&
            this.loginController?.loggedIn &&
            this._claimState === ClaimStateTypes.available &&
            this._codeStatus?.status === MintStatus.available &&
            this.loginFlow
        ) {
            this.emit(LoginEvents.Submit, {
                bubbles: true,
                composed: true,
                detail: {
                    ...this.loginController?.optinDetails,
                    email: this.loginController?.user?.email,
                    userEmail: this.loginController?.user?.email,
                    wallet: this.loginController?.user?.walletAddress,
                    did: this.loginController?.user?.did?.token,
                },
            })
            if (
                this.loginController?.callerId === this.callerId
                || (
                    this.loginController?.callerId
                    && this.loginController?.callerId.length > 0
                    && this.checkCallerIdMatch(this.loginController.callerId)
                )
            ) {
                this.handleClaim()
            } else {
                const walletCheckResponse = await this.setSessionToken()
                if (walletCheckResponse && walletCheckResponse.data?.holds) {
                    this.setPageNumber(3)
                    this._claimState = ClaimStateTypes.redeemedLogin
                } else if (!walletCheckResponse) {
                    this._claimState = ClaimStateTypes.availableLoggedIn
                }
            }
        } else if (
            this.loginController?.loginInit &&
            this.loginController?.loggedIn &&
            this._claimState !== ClaimStateTypes.redeemedLogin &&
            this._claimState !== ClaimStateTypes.minting
        ) {
            const walletCheckResponse = await this.setSessionToken()
            if (walletCheckResponse && walletCheckResponse.data?.holds) {
                this.setPageNumber(3)
                this._claimState = ClaimStateTypes.redeemedLogin
            } else if (!walletCheckResponse) {
                this._claimState = ClaimStateTypes.availableLoggedIn
            }
        } else if (
            this.loginController?.loginInit &&
            !this.loginController?.loggedIn &&
            this._claimState === ClaimStateTypes.validating &&
            this._codeStatus?.status === MintStatus.available &&
            !this.loginController?.loggedIn
        ) {
            this.loginFlow = true
            this._claimState = ClaimStateTypes.available
        }

        if (
            this.loginController?.loginInit &&
            this._claimState === ClaimStateTypes.redeemedLoggedout &&
            this.loginController?.loggedIn &&
            this.loginController?.user?.walletAddress ===
                this._codeStatus?.wallet
        ) {
            this.setSessionToken().then(() => {
                this.setPageNumber(3)
                this._claimState = ClaimStateTypes.redeemedLogin
            })
        } else if (
            this.loginController?.loginInit &&
            this._claimState === ClaimStateTypes.redeemedLoggedout &&
            this.loginController?.loggedIn &&
            this.loginController?.user?.walletAddress !==
                this._codeStatus?.wallet
        ) {
            this._claimState = ClaimStateTypes.redeemedLoggedInInvalid
        }

        if (
            (this._claimState === ClaimStateTypes.redeemedLogin ||
                this._claimState === ClaimStateTypes.availableLoggedIn) &&
            this.loginController?.loginInit &&
            !this.loginController?.loggedIn
        ) {
            this.unlockedContent = false
            this.loginFlow = true
            this.walletCheckResponse = null
            if (this._codeStatus?.status === MintStatus.available) {
                this._claimState = ClaimStateTypes.available
            } else {
                this._claimState = ClaimStateTypes.invalid
            }
        }

        if (this._claimState === ClaimStateTypes.minting && this.mintRequestDid) {
            const status = this.n3xusAPIController?.getMintRequestStatus(
                this.mintRequestDid,
                this.redeemCode,
                this.campaignId || undefined
            );
           
            if (!status) {
                this.error = 'an error occured'
                this._claimState = ClaimStateTypes.error
                throw new Error('error occured')
            } else {
                if (
                    status.status === 'completed'
                ) {
                    this.mintRequestDid = null
                    clearStoredSession()
                    this.walletCheckResponse = null
                    this.walletCheckResponse = await this.setSessionToken(true)
                    this.success = true
                    if (
                        this.loginController?.loginInit &&
                        this.loginController?.loggedIn
                    ) {
                        this.setPageNumber(3)
                        this._claimState = ClaimStateTypes.redeemedLogin
                    } else {
                        this._claimState = ClaimStateTypes.redeemedLoggedout
                    }

                    this.emit(ClaimEvents.Claimed, {
                        bubbles: true,
                        composed: true,
                        detail: {
                            sessionToken:
                                this.walletCheckResponse?.data?.sessionToken,
                            email: this.walletCheckResponse?.data?.email,
                            apiKey: this.apiKey,
                            holds: this.walletCheckResponse?.data?.holds,
                            mobileWallet:
                                this.walletCheckResponse?.data?.mobilePasses,
                            wallet: this.loginController?.user?.walletAddress,
                            did: this.loginController?.user?.did?.token,
                        },
                    })
                    this.unlockedContentEmit()
                } else if (status.status === 'redeemed') {
                    clearStoredSession()
                    this.mintRequestDid = null
                    this.walletCheckResponse = null
                    this.walletCheckResponse = await this.setSessionToken(true)
                    if(status.error === ALREADY_MINTED_ERROR) {
                        this._alreadyClaimed = true
                        this._claimState = ClaimStateTypes.redeemedLogin

                    } else if (
                        this.walletCheckResponse?.data?.holds &&
                        this.loginController?.loggedIn
                    ) {
                        this._claimState = ClaimStateTypes.redeemedLogin
                        
                    } else {
                        this._claimState = ClaimStateTypes.redeemedLoggedInInvalid
                    }
                    this.setPageNumber(3)
                } else if (
                    status.status === 'failed'
                ) {
                    this.setPageNumber(3)
                    this.mintRequestDid = null
                    this._claimState = ClaimStateTypes.mintingError
                    this.error = 'Claim failed. Please try again later.'
                }
            }
        }
    }

    private async checkSessionStatus() {
        this.logging('checkSessionStatus')
        const session = getLatestSession(this.apiKey)
        this.logging('session', session) 
        const threeMinutesFortyFiveSeconds = 3.5 * 60 * 1000 // 3 minutes 45 seconds in milliseconds
        const minutesAgo = Date.now() - threeMinutesFortyFiveSeconds
        this.logging('minutesAgo', minutesAgo)
    
        if (session && session.issueTS > minutesAgo) {
            // Session is still valid, no need to make API call
            this.logging('session is still valid, no need to make API call')
            return
        }

        this.logging('session is expired, making API call')
        // Only make API call if session is expired or doesn't exist
        this.setSessionToken()
    }

    // Start polling every 30 seconds
    private startPolling() {
        if (!this.interval) {
            this.interval = setInterval(() => this.checkSessionStatus(), 30000)
         }
    }

    // Stop polling
    private stopPolling() {
        if (this.interval) {
            clearInterval(this.interval)
            this.interval = null
        }
    }    

    /**
     * Sets the token status by checking its validity and updates the claim state accordingly.
     *
     * @param code - The code to be checked.
     * @returns A promise that resolves to the status data of the code.
     * @throws Will throw an error if the Nexus API is not initialized.
     *
     * The function performs the following steps:
     * 1. Checks if the Nexus API is initialized.
     * 2. Retrieves the code status from the Nexus API.
     * 3. Updates the internal code status and logs the status.
     * 4. Updates the claim state based on the code status.
     * 5. Emits an event if the code status is not okay.
     *
     * The claim state is updated based on the following conditions:
     * - If the code is available and the user is logged in, the state is set to `availableLoggedIn`.
     * - If the code is available and the user is not logged in, the state is set to `validating`.
     * - If the code is redeemed and the user is logged in with the correct wallet address, the state is set to `redeemedLogin`.
     * - If the code is redeemed and the user is logged in with an incorrect wallet address, the state is set to `redeemedLoggedInInvalid`.
     * - If the code is minting, the state is set to `minting`.
     * - If there is an error with the code, the state is set to `mintingError`.
     * - If the code status is invalid, an error message is set and the state is set to `invalid`.
     */
    private async setCodeStatus(
        issueId: number | null = null,
        redeemCode: string | null = null
    ): Promise<void> {
        if (!this.n3xusAPIController) {
            throw new Error('Nexus API is not initialized')
        }
        let status = null
        if (issueId) {
            status = await this.n3xusAPIController?.checkIssueStatus(
                this.n3xusApiConfig!,
                issueId,
                redeemCode || null
            )
        } else if (redeemCode) {
            this.redeemCode = redeemCode
            status = await this.n3xusAPIController?.checkIssueStatus(
                this.n3xusApiConfig!,
                (issueId = null),
                (redeemCode = redeemCode)
            )
        }
        if (!status) {
            this.logging('Code status updated', status)
            this._claimState = ClaimStateTypes.error
        } else {
            this.logging('setCodeStatus', issueId)
            this._codeStatus = status.data
            this.logging('Code Status', status)
            if (!status.ok) {
                this._claimState = ClaimStateTypes.error
            }
            switch (status.data?.status) {
                case MintStatus.available:
                    if (
                        this.loginController?.loginInit &&
                        this.loginController?.loggedIn
                    ) {
                        this.setSessionToken().then(() => {
                            this._claimState = ClaimStateTypes.availableLoggedIn
                        })
                    } else if (
                        this.loginController?.loginInit &&
                        !this.loginController?.loggedIn
                    ) {
                        this._claimState = ClaimStateTypes.validating
                    }
                    break
                case MintStatus.redeemed:
                    if (!this.loginController?.loginInit) {
                        this._claimState = ClaimStateTypes.validating
                    } else if (
                        this.loginController?.loginInit &&
                        this.loginController?.loggedIn &&
                        this.loginController?.user?.walletAddress ===
                            status.data?.wallet
                    ) {
                        this.setSessionToken().then(() => {
                            this.setPageNumber(3)
                            this._claimState = ClaimStateTypes.redeemedLogin
                        })
                    } else if (
                        this.loginController?.loginInit &&
                        this.loginController?.loggedIn &&
                        this.loginController?.user?.walletAddress !==
                            status.data?.wallet
                    ) {
                        this._claimState =
                            ClaimStateTypes.redeemedLoggedInInvalid
                    } else {
                        //Or just error
                        this._claimState =
                            ClaimStateTypes.redeemedLoggedInInvalid
                    }
                    break
                case MintStatus.minting:
                    this._claimState = ClaimStateTypes.minting
                    break
                case MintStatus.error:
                    this._claimState = ClaimStateTypes.mintingError
                    break
                default:
                    this.error =
                        'Seems like your link is invalid, try again or contact support'
                    this._claimState = ClaimStateTypes.invalid
                    break
            }
        }
    }

    private async getCampaignInformation(
        campaignId: number
    ): Promise<CampaignInformation> {

        try {
            const { data, error } =
                await this.n3xusAPIController?.getCampaignInformation(
                    this.n3xusApiConfig!,
                    campaignId
                ) || {}
            if (error || !data) {
                this.error =
                    error ||
                    'An error occurred while fetching campaign information'
                this._claimState = ClaimStateTypes.error
                throw new Error(this.error)
            }

            return data
        } catch (err) {
            this.error =
                err instanceof Error
                    ? err.message
                    : 'An error occurred while fetching campaign information'
            this._claimState = ClaimStateTypes.error
            throw err
        }
    }

    private async setCampaignStatus(): Promise<void> {
        const validation = await this.n3xusAPIController?.checkCampaign(
            this.n3xusApiConfig!,
            this.campaignUrl
        )
        if (!validation) {
            this.error = 'an error occured'
            this._claimState = ClaimStateTypes.error
            throw new Error('error occured')
        }

        if (validation.ok && validation.data.available) {
            this.availibleCampaign = true
        }
    }

    /**
     * Handles the claim process for a Fan Pass.
     *
     * This method performs the following steps:
     * 1. Retrieves the DID token from the login controller.
     * 2. Checks if the redeem code is provided.
     * 3. If so use code to mint, else use the current url as a campaign url
     * 4. Attempts to mint the Fan Pass using the Nexus API.
     * 5. Sets the claim state to minting and calculates the maximum attempts for validation based on the estimated time.
     * 6. Validates the minting process at regular intervals.
     *
     * If an error occurs during the minting process, it sets an appropriate error message.
     *
     * @throws Will set an error message if the redeem token is not provided or if an error occurs during the minting process.
     */
    private async handleClaim() {
        this._claimState = ClaimStateTypes.minting
        let didToken = this.loginController?.user?.did?.token as string
        if (didToken === undefined) {
            await this.loginController?.getDidToken()
            didToken = this.loginController?.user?.did?.token as string
        }

        try {
            this.emit(ClaimEvents.Claiming, {
                bubbles: true,
                composed: true,
                detail: {
                    ...this.loginController?.optinDetails,
                    email: this.loginController?.user?.email,
                    apiKey: this.apiKey,
                    wallet: this.loginController?.user?.walletAddress,
                    did: this.loginController?.user?.did?.token,
                },
            })

            // Handle invalid input case first
            if (this.redeemCode === '' && !this.availibleCampaign) {
                this.error =
                    'Seems like your link is invalid, try again or contact support'
                return
            }

            // Determine parameters and make mint call
            this.mintRequestDid = didToken
            this.n3xusAPIController?.mint(
                this.n3xusApiConfig!,
                didToken,
                this.redeemCode || undefined,
                this.availibleCampaign ? this.campaignUrl : undefined,
                this.campaignId || undefined
            )
        } catch (error: any) {
            // Handle specific error cases
            switch (error.message) {
                case 'Issuance code not available':
                    this._claimState = ClaimStateTypes.error
                    this.error = 'This code not available or already used!'
                    break
                case 'campaign does not exist':
                    this._claimState = ClaimStateTypes.error
                    this.error = 'Use a code to claim your FanPasss'
                    break
                case 'Failed to fetch code status: You already have this campaign':
                    this._claimState = ClaimStateTypes.redeemedLogin
                    break
                default:
                    this._claimState = ClaimStateTypes.error
                    this.error =
                        'An error occurred while claiming the Fan Pass. Please try again later.'
            }
        }
    }

    private unlockedContentEmit() {
        if (!this.unlockedContent) {
            this.unlockedContent = true
            this.emit(UnlockEvents.UnlockComplete, {
                bubbles: true,
                composed: true,
                detail: {
                    sessionToken: this.walletCheckResponse?.data?.sessionToken,
                    email: this.walletCheckResponse?.data?.email,
                    apiKey: this.apiKey,
                    unlocked: true,
                    wallet: this.loginController?.user?.walletAddress,
                    did: this.loginController?.user?.did?.token,
                    expiryDate: this.walletCheckResponse?.data?.expiryDate,
                },
            })
        }
    }

    /**
     * Sets the session token by checking the claim state and interacting with the Nexus API.
     *
     * @returns {Promise<void>} A promise that resolves when the session token is set.
     *
     * @remarks
     * This method performs the following steps:
     * 1. Checks if the claim state is `redeemedLogin`.
     * 2. Retrieves the stored session, if available, and logs it.
     * 3. Initializes the Nexus API if not already initialized.
     * 4. Retrieves user information if not already available.
     * 5. Retrieves the DID token if not already available.
     * 6. Checks the wallet for an NFT using the DID token.
     * 7. Stores the session token if the wallet holds the NFT.
     *
     * @throws Will log an error if no DID token is found or if checking the wallet for an NFT fails.
     */
    private async setSessionToken(bypassCache: boolean = false): Promise<AccessCheckData | null> {
        if (!this.loginController?.loginInit) {
            this.stopPolling()
            clearStoredSession()
            return null
        }
        if (!this.loginController?.loggedIn) {
            this.stopPolling()
            clearStoredSession()
            return null
        }

        if (!this.n3xusAPIController) {
            this.n3xusApiConfig = {
                apiKey: this.apiKey,
                environment: this.environment,
            }
        }
        if (!this.loginController?.user) {
            await this.loginController?.getUserInfo()
        }
        if (!this.loginController?.user?.did?.token) {
            await this.loginController?.getDidToken()
        }
        if (!this.loginController?.user?.did?.token) {
            console.error('No DID token found')
            return null
        }

        await this.loginController?.getDidToken()
        this.walletCheckResponse = await this.n3xusAPIController?.checkWalletForNft(
            this.n3xusApiConfig!,
            '',
            this.loginController?.user?.did?.token,
            bypassCache
        )

        if (!this.walletCheckResponse?.ok) {
            console.error('Check wallet for NFT failed')
            return null
        }
        if (
            this.walletCheckResponse.data?.holds &&
            this.loginController?.loggedIn
        ) {
            // Add new session data to list
            addSession({
                didToken: this.loginController?.user?.did?.token,
                issueTS: Date.now(),
                token: this.walletCheckResponse.data.sessionToken,
                apiKey: this.apiKey,
                mobilePass: this.walletCheckResponse.data.mobilePasses,
                wallet: this.loginController?.user?.walletAddress,
                email: this.loginController?.user?.email,
            })
            this.unlockedContentEmit()
            if (this.interval === null) {
                this.startPolling()
            }
            return this.walletCheckResponse
        }
        return null
    }

    public async openClaim() {
        const overlay = this.shadowRoot?.getElementById('fb-modal') as Overlay
        if (overlay) {
            overlay.openOverlay()
            this.emit(UnlockEvents.ModalOpen, {
                bubbles: true,
                composed: true,
            })
        }
    }

    /**
     * Logs out the current user by calling the logout method on the loginController.
     * After logging out, it sets the claim state to available.
     *
     * @returns {Promise<void>} A promise that resolves when the logout process is complete.
     */
    public async logout() {
        this.unlockedContent = false
        this.walletCheckResponse = null
        this.loginFlow = true
        this._claimState = ClaimStateTypes.available
        this._pageNumber = 1
        await this.loginController?.logout()
        this.stopPolling()
        clearStoredSession()
    }

    /**
     * Closes the overlay and updates the trigger button's aria-expanded attribute.
     *
     * @param e - The event object triggered by the action.
     */
    public closeOverlay(e: Event | null = null): void {
        const overlay = this.shadowRoot?.getElementById('fb-modal') as Overlay
        const triggerButton = e?.target as Button
        triggerButton?.setAttribute('aria-expanded', 'false')
        overlay?.closeOverlay()
        this.emit(UnlockEvents.ModalClose, {
            bubbles: true,
            composed: true,
        })
    }

    /**
     * Opens the overlay and updates the claim state if the code status is null.
     *
     * @param e - The event that triggered the overlay to open.
     *
     * This method performs the following actions:
     * 1. Retrieves the overlay element from the shadow DOM.
     * 2. Sets the `aria-expanded` attribute of the trigger button to 'true'.
     * 3. If the code status is null, it sets the claim state to validating and calls `setCodeStatus` with the redeem code.
     * 4. Opens the overlay.
     */
    public openOverlay(e: Event | null = null): void {
        const overlay = this.shadowRoot?.getElementById('fb-modal') as Overlay;
        const triggerButton = e?.target as Button;
        triggerButton?.setAttribute('aria-expanded', 'true');
        if (overlay) {
            overlay.openOverlay();
            window.addEventListener('mousedown', this.onClaimModalClickOut);
        }
    }

    private onClaimModalClickOut = (evt: MouseEvent) => {
        // Start from the overlay element, not this.shadowRoot
        const overlay = this.shadowRoot?.getElementById('fb-modal') as HTMLElement | null;
        if (!overlay) return;
        const boxNode = findBoxNode(overlay);
        if (!boxNode) return;
        if (!evt.composedPath().includes(boxNode)) {
            this.closeOverlay(evt);
            window.removeEventListener('mousedown', this.onClaimModalClickOut);
        }
    }

    /**
     * Logs a message to the console if debug logging is enabled.
     *
     * @param message - The message to log.
     * @param optionalParams - Additional optional parameters to log.
     */
    private logging(message?: any, ...optionalParams: any[]): void {
        if (this.debugLogging) {
            console.log(message, optionalParams)
        }
    }

    private renderMobilePass(): TemplateResult {
        if (
            this.walletCheckResponse?.data?.mobilePasses &&
            Array.isArray(this.walletCheckResponse?.data?.mobilePasses) &&
            this.walletCheckResponse?.data?.mobilePasses.length > 0 &&
            !this.walletCheckResponse?.data?.mobilePasses[
                this.walletCheckResponse?.data?.mobilePasses.length - 1
            ].isActivated
        ) {
            const mobilePass =
                this.walletCheckResponse?.data?.mobilePasses[
                    this.walletCheckResponse?.data?.mobilePasses.length - 1
                ]
            return html`
                <fp-mobile-pass
                    exportparts="
                    base-text,
                    base-title,
                    base-body,
                    mobile-pass,
                    mobile-pass-title
                    mobile-pass-action-text
                    mobile-pass-link,
                    apple-wallet-image,
                    google-wallet-image,
                    wallet-buttons"
                    .fanPassTitle=${this.fanPassTitle}
                    .androidLink=${mobilePass?.androidWalletUri}
                    .appleLink=${mobilePass?.iPhoneWalletUri}
                    .downloadLink=${mobilePass?.downloadLink}
                    .displayIntro=${false}
                ></fp-mobile-pass>
            `
        }
        return html``
    }

    /**
     * Renders the success message and buttons after a Fan Pass has been successfully claimed.
     *
     * @returns {TemplateResult} The HTML template for the success message and buttons.
     *
     * The rendered template includes:
     * - A paragraph indicating the Fan Pass has been collected.
     * - A button to redirect the user to check out their perks.
     * - A button to log out the user.
     */
    private renderClaimSuccess(): TemplateResult {
        this.setPageNumber(3)
        return html`
            <pu-vertical-stack
                exportparts="base:success"
                part="base-success"
                spacing="24"
                spacingNarrow="24"
                >
                <pu-text
                    exportparts="base:connected-title"
                    part="base-connected-title base-text base-title"
                    as="p"
                    variant="bodyLg"
                    withoutMargin
                >
                    ${this._alreadyClaimed ? `You already have this ${this.fanPassTitle}` : this.claimSuccessText.replace('${fanPassTitle}', this.fanPassTitle)}
                </pu-text>
                <pu-vertical-stack
                    exportparts="base:perks"
                    part="base-perks"
                    spacing="8"
                    spacingNarrow="8"
                    >
                    ${this.successUrl !== ''
                        ? html`
                              <pu-button
                                exportparts="button,button:perks-button"
                                part="base-buttonbase-perks-button"
                                class="button perks-button"
                                url=${this.successUrl}
                                target="_blank"
                                size="large"
                                variant="solid"
                              >
                                <pu-text
                                    exportparts="base:button-label,base:perks-button-label"
                                    part="base-button-label base-perks-button-label"
                                    as="p"
                                    variant="bodyMd"
                                    withoutMargin
                                >
                                    ${this.claimSuccessPerkButtonText.replace('${fanPassTitle}', this.fanPassTitle)}
                                </pu-text>
                              </pu-button>
                          `
                        : null}
                    ${this.renderMobilePass()}
                </pu-vertical-stack>
            </pu-vertical-stack>
        `
    }

    /**
     * Sets the page number for the pagination dots.
     * If no number param is given (e.g. this.setPageNumber()) the number will be set based on the current magic login state.
     * If number param is given (e.g. this.setPageNumber(3)) it will set page number to the number specified
     * @param number
     *
     */
    private setPageNumber(number?: number) {
        if (number !== undefined) {
            this._pageNumber = number
            return
        }

        //TODO: THIS NEEDS TO BE CHANGED!! To be based on the claim state
        const loginState = this._loginComponent?.getCurrentState()
        switch (loginState) {
            case LoginStateTypes.initialising:
            case LoginStateTypes.initialised:
                this._pageNumber = 1
                break
            case LoginStateTypes.requestCode:
            case LoginStateTypes.enterLoginCode:
                this._pageNumber = 2
                break
            case LoginStateTypes.optCodeSubmited:
            case LoginStateTypes.isLoggedInLoading:
                this._pageNumber = 3
                break
            case LoginStateTypes.optFailed:
            case LoginStateTypes.isLoggedIn:
                this._pageNumber = 3
                break
            case LoginStateTypes.isLoggedIn:
                this._pageNumber = 3
                break
            default:
                this._pageNumber = 1
                break
        }
    }

    /**
     * Renders the content based on the current claim state.
     *
     * @returns {TemplateResult} The template result to be rendered.
     *
     * The content rendered depends on the value of `this._claimState`:
     * - `ClaimStateTypes.redeemedLogin`: Renders the claim success content.
     * - `ClaimStateTypes.redeemedLoggedout`: Renders a login component with specific attributes.
     * - `ClaimStateTypes.validating`: Renders a "Validating..." message.
     * - `ClaimStateTypes.invalid`: Renders an error message.
     * - `ClaimStateTypes.avaialableLoggedIn`: Renders the claim UI for logged-in users.
     * - `ClaimStateTypes.avaialable`: Renders the claim UI for users who need to log in.
     * - `ClaimStateTypes.minting`: Renders a "Minting..." message.
     * - `ClaimStateTypes.redeemedLoggedInInvalid`: Renders a message indicating the token has already been used.
     * - `ClaimStateTypes.redeemed`: Renders different content based on the login state and token status.
     * - `ClaimStateTypes.mintingError`: Renders an error message.
     * - `ClaimStateTypes.error` or default: Renders a "Status Error" message.
     */
    private renderContent(): TemplateResult {
        this.setPageNumber()

        switch (this._claimState) {
            case ClaimStateTypes.redeemedLogin:
                this.setPageNumber(3)
                return this.renderClaimSuccess()
            case ClaimStateTypes.redeemedLoggedout:
                this.setPageNumber(3)
                return html`
                    <pu-vertical-stack
                        exportparts="base:redeemed-logged-out-layout"
                        part="base-redeemed-logged-out-layout"
                        spacing="24"
                        align="center"
                        alignNarrow="center"
                    >
                        <pu-text
                            exportparts="base:redeemed-logged-out-text"
                            part="base-redeemed-logged-out-text base-text base-body"
                            as="p"
                            variant="bodyLg"
                            withoutMargin
                            >
                            This ${this.fanPassTitle} has already been claimed
                        </pu-text>
                    </pu-vertical-stack>
                `
            case ClaimStateTypes.redeemedCheckingSession:
            case ClaimStateTypes.validating:
                return html`
                    <pu-text
                        exportparts="base:initialising-text"
                        part="base-initialising-text base-text base-body"
                        as="p"
                        variant="bodyLg"
                        withoutMargin
                        > Just a moment… </pu-text>
                    <svg
                        part="initialising-spinner"
                        class="loading__spinner"
                        width="16"
                        height="16"
                        xmlns="http://www.w3.org/2000/svg"
                        focusable="false"
                        role="presentation"
                        aria-hidden="true"
                        viewBox="0 0 512 512"
                    >
                        <path
                            d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                        />
                    </svg>
                `
            case ClaimStateTypes.invalid:
                this.setPageNumber(3)
                return html`
                    <fp-error-screen
                        errorText=${this.error}
                        faqUrl=${this.faqUrl}
                    ></fp-error-screen>
                `
            case ClaimStateTypes.availableLoggedIn:
                return html`
                    <pu-vertical-stack
                        exportparts="base:valid-login-layout"
                        part="base-valid-login-layout"
                        spacing="24"
                        align="center"
                        alignNarrow="center"
                    >
                        <pu-text
                            exportparts="base:valid-login-title"
                            part="base-valid-login-title base-text base-body"
                            as="p"
                            variant="bodyLg"
                            withoutMargin
                            >
                            ${this.claimIntroText.replace('${fanPassTitle}', this.fanPassTitle)}
                        </pu-text>
                        <pu-text
                            exportparts="base:valid-login-user-email"
                            part="base-valid-login-user-email base-text base-body"
                            as="p"
                            variant="bodyMd"
                            withoutMargin
                            >
                            Connected as ${this.loginController?.user?.email}
                        </pu-text>
                        <pu-vertical-stack
                            exportparts="base:claim-account-layout"
                            part="base-claim-account-layout"
                            spacing="8"
                            align="center"
                            alignNarrow="center"
                        >
                            <pu-button
                                exportparts="button,button:claim-account-button"
                                part="base-button base-claim-account-button"
                                class="button"
                                variant="solid"
                                size="large"
                                fullWidth
                                @click="${this.handleClaim}"
                            >
                                <pu-text
                                    exportparts="base:button-label,base:claim-account-button-label"
                                    part="base-button-label base-claim-account-button-label"
                                    as="span"
                                    variant="bodyMd"
                                    withoutMargin
                                >
                                    Claim to this account
                                </pu-text>
                            </pu-button>
                            <pu-button
                                exportparts="button,button:change-account-button"
                                part="base-button base-change-account-button"
                                class="button"
                                @click="${this.logout}"
                                variant="solid"
                                size="large"
                                fullWidth
                            >
                                <pu-text
                                    exportparts="base:button-label,base:change-account-button-label"
                                    part="base-button-label base-change-account-button-label"
                                    as="span"
                                    variant="bodyMd"
                                    withoutMargin
                                >
                                    Change account
                                </pu-text>
                            </pu-button>
                        </pu-vertical-stack>
                    </pu-vertical-stack>
                `
            case ClaimStateTypes.available:

                return html`
                    <pu-vertical-stack
                        exportparts="base:flow-form-layout"
                        part="base-flow-form-layout"
                        class=""
                        spacing="24"
                        align="center"
                        alignNarrow="center"
                    >
                        ${this._pageNumber === 1
                            ? html`<pu-text
                                  exportparts="base:disconnected-title"
                                  part="base-disconnected-title base-text title"
                                  as="p"
                                  variant="bodyLg"
                                  withoutMargin
                              >
                                  ${this.claimIntroText.replace('${fanPassTitle}', this.fanPassTitle)}
                              </pu-text>`
                            : html``}
                        <fp-login
                            id="login"
                            callerId=${this.callerId}
                            buttonLabel=${this.claimButtonText}
                            apiKey=${this.apiKey}
                            ?hidden=${false}
                            environment=${this.environment}
                            ?debugLogging=${this.debugLogging}
                            ?showLoaderAfterLogin=${true}
                            exportparts="${loginShadowParts}"
                            >
                            <div slot="form-content-before-submit">    
                                <slot name="form-content-claim-before-submit"></slot>
                            </div>
                            <form part="form-content" slot="form-content">
                                    <slot name="form-content-claim"></slot>
                            </form>
                        </fp-login>
                    </pu-vertical-stack>
                `
            case ClaimStateTypes.minting:
                return html`
                    <pu-text as="p" variant="bodyLg" withoutMargin part="claiming-title">
                        ${this.claimingText.replace('${fanPassTitle}', this.fanPassTitle)}
                    </pu-text>
                    <svg
                        part="loading__spinner"
                        class="loading__spinner"
                        width="16"
                        height="16"
                        xmlns="http://www.w3.org/2000/svg"
                        focusable="false"
                        role="presentation"
                        aria-hidden="true"
                        viewBox="0 0 512 512"
                    >
                        <path
                            d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                        />
                    </svg>
                `
            case ClaimStateTypes.redeemedLoggedInInvalid:
                this.setPageNumber(3)
                return html`
                    <fp-error-screen
                        errorText="This code has already been used"
                    >
                        <pu-button
                            exportparts="button,button:restart-button"
                            part="base-button base-restart-button"
                            slot="button"
                            @click="${this.logout}"
                            type="button"
                            class="button"
                            name="disconnect"
                            size="large"
                            variant="solid"
                        >
                            <pu-text
                                exportparts="base:button-label,base:restart-button-label"
                                part="base-button-label base-restart-button-label"
                                as="span"
                                variant="bodyMd"
                                fontWeight="regular"
                                withoutMargin
                                spacing="loose"
                            >
                                Start over
                            </pu-text>
                        </pu-button>
                    </fp-error-screen>
                `

            case ClaimStateTypes.redeemed:
                if (!this.loginController?.loginInit) {
                    this.setPageNumber(2)
                    return html`
                        <pu-text as="p" variant="bodyLg" withoutMargin>
                            Loading...
                        </pu-text>
                        <svg
                            part="loading__spinner"
                            class="loading__spinner"
                            width="16"
                            height="16"
                            xmlns="http://www.w3.org/2000/svg"
                            focusable="false"
                            role="presentation"
                            aria-hidden="true"
                            viewBox="0 0 512 512"
                        >
                            <path
                                d="M463.9 376c7.6 4.4 17.5 1.8 21.4-6.1c17.1-34.3 26.7-73 26.7-113.9C512 120 405.9 8.8 272 .5c-8.8-.5-16 6.7-16 15.5v0c0 8.8 7.2 15.9 16 16.6C388.2 40.8 480 137.7 480 256c0 35.1-8.1 68.3-22.5 97.9c-3.9 8-1.3 17.7 6.4 22.2v0z"
                            />
                        </svg>
                    `
                }

                if (!this.loginController?.loggedIn) {
                    return html`
                        <fp-login
                            id="login"
                            callerId=${this.callerId}
                            buttonLabel=${this.claimButtonText}
                            apiKey=${this.apiKey}
                            ?hidden=${false}
                            environment=${this.environment}
                            ?debugLogging=${this.debugLogging}
                            ?showLoaderAfterLogin=${true}
                            exportparts="${loginShadowParts}"
                        ></fp-login>
                    `
                } else if (
                    this._codeStatus?.wallet == this._user?.walletAddress
                ) {
                    this.setPageNumber(3)
                    return html`
                        <pu-vertical-stack spacing="24">
                            <pu-text as="p" variant="bodyLg" withoutMargin part="claimed-title">
                                You've claimed your ${this.fanPassTitle}!
                            </pu-text>
                            <pu-button
                                exportparts="button,button:perks-button"
                                part="base-button base-perks-button"
                                class="button"
                                url=${this.successUrl}
                                target="_blank"
                            >
                                <pu-text
                                    exportparts="base:button-label,base:perks-button-label"
                                    part="base-button-label base-perks-button-label"
                                    as="p"
                                    variant="bodyMd"
                                    withoutMargin
                                >
                                    ${this.claimSuccessPerkButtonText.replace('${fanPassTitle}', this.fanPassTitle)}
                                </pu-text>
                            </pu-button>
                        </pu-vertical-stack>
                    `
                }

                this.setPageNumber(3)
                return html`
                    <fp-error-screen
                        errorText="Invalid ${this.fanPassTitle}"
                        faqUrl=${this.faqUrl}
                    ></fp-error-screen>
                `
            case ClaimStateTypes.mintingError:
                return html`
                    <fp-error-screen
                        errorText=${this.error}
                        faqUrl=${this.faqUrl}
                    ></fp-error-screen>
                `
            case ClaimStateTypes.error:
            default:
                this.setPageNumber(3)
                return html`
                    <fp-error-screen
                        errorText=${this.error || 'Claiming error'}
                        faqUrl=${this.faqUrl}
                    >
                        ${this._campaignState == CampaignState.available
                            ? html`
                                  <pu-button
                                      exportparts="button,button:restart-button"
                                      part="base-button base-restart-button"
                                      slot="button"
                                      @click="${this.logout}"
                                      type="button"
                                      class="button"
                                      name="disconnect"
                                      size="large"
                                      variant="solid"
                                  >
                                      <pu-text
                                          exportparts="base:button-label,base:restart-button-label"
                                          part="base-button-label base-restart-button-label"
                                          as="span"
                                          variant="bodyMd"
                                          fontWeight="regular"
                                          spacing="loose"
                                          withoutMargin
                                      >
                                          Start over
                                      </pu-text>
                                  </pu-button>
                              `
                            : ''}
                    </fp-error-screen>
                `
        }
    }

    private renderClaimButton(): TemplateResult {
        if (this.hideClaimButton) {
            return html``
        }
        let buttonLabel = this.claimButtonText
        let buttonPart = 'disconnected-button'
        switch (this._claimState) {
            case ClaimStateTypes.redeemedLogin:
                this._pageNumber = 3
                buttonLabel = 'Claimed'
                buttonPart = 'connected-button'
                break
            case ClaimStateTypes.redeemedLoggedout:
                buttonLabel = 'Claimed'
                buttonPart = 'connected-button'
                this._pageNumber = 1
                break
            case ClaimStateTypes.validating:
                this._pageNumber = 1
                buttonLabel = 'Just a moment…'
                break
            case ClaimStateTypes.minting:
                this._pageNumber = 2
                buttonLabel = 'Claiming...'
                break
            case ClaimStateTypes.mintingError:
                this._pageNumber = 3
                // buttonLabel = 'Claim failed'
                buttonLabel = this.claimButtonText
                break
            case ClaimStateTypes.error:
                this._pageNumber = 3
                // buttonLabel = 'Status Error'
                buttonLabel = this.claimButtonText
                break
            default:
                this._pageNumber = 1
                buttonLabel = this.claimButtonText
                break
        }
        return html`
            <div part="claim-button-container" class="claim-button-container">
                <pu-button
                    exportparts="button,button:${buttonPart}"
                    part="base-button base-${buttonPart}"
                    class="button"
                    @click=${this.openOverlay}
                    type="button"
                    name="claim"
                    size="large"
                    fullWidth
                    variant="solid"
                    ?disabled=${this._claimState ===
                        ClaimStateTypes.validating ||
                    this._claimState ===
                        ClaimStateTypes.redeemedCheckingSession}
                    ?loading=${this._claimState ===
                        ClaimStateTypes.validating ||
                    this._claimState ===
                        ClaimStateTypes.redeemedCheckingSession}
                >
                    <pu-text
                        exportparts="base:button-label,base:${buttonPart}-label"
                        part="base-button-label base-${buttonPart}-label"
                        as="span"
                        variant="bodyMd"
                        withoutMargin
                        spacing="loose"
                    >
                        ${buttonLabel} 
                    </pu-text>
                </pu-button>
            </div>
        `
    }

    /**
     * Renders the component's template.
     *
     * This method returns a `TemplateResult` which represents the HTML structure of the component.
     * If the `modal` property is true, it renders a button that opens an overlay modal when clicked.
     * The modal contains additional content and a close button.
     * If the `modal` property is false, it renders the default content.
     *
     * @returns {TemplateResult} The HTML template for the component.
     */
    public render(): TemplateResult {
        this.logging('render', 'claimState', this._claimState)
        if (this.modal) {
            return html`
                ${this.walletCheckResponse && this.loginController?.loggedIn && !this.hideConnected
                    ? html`
                          <fp-connected
                                exportparts="${connectedShadowParts}"
                                displayText=${this.walletCheckResponse.data?.holds
                                    ? "You're connected!"
                                    : this.claimIntroText.replace('${fanPassTitle}', this.fanPassTitle) }
                                fanPassTitle=${this.fanPassTitle}
                                apiKey=${this.apiKey}
                                environment=${this.environment}
                                ?debugLogging=${this.debugLogging}
                                ?ownsFp=${this.walletCheckResponse.data?.holds
                                    ? true
                                    : false}
                            >
                              <div part="inner-content" slot="inner-content">
                                  ${this.renderClaimButton()}
                              </div>
                          </fp-connected>
                      `
                    : html` ${this.renderClaimButton()} `}

                <pu-overlay id="fb-modal" class="fb-modal" part="overlay-container" exportparts="base,close,content">
                    <pu-box
                        part="modal"
                        exportparts="base:modal-container,content:modal-content"
                        slot="content"
                        class="modalContentBox"
                        fit="fit-content"
                        fitNarrow="fill"
                    >
                        <button
                            part="button-icon"
                            style="position: absolute; top: 0.5em; right: 0.5em; z-index: 501"
                            class="button-icon"
                            type="button"
                            aria-label="Close modal"
                            aria-controls="overlay"
                            @click=${this.closeOverlay}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                id="close"
                                part="icon"
                                class="icon"
                                width="1em"
                                height="1em"
                                viewBox="0 0 384 512"
                                role="presentation"
                                aria-hidden="true"
                                focusable="false"
                                fill="currentColor"
                            >
                                <path
                                    d="M192 233.4L59.5 100.9 36.9 123.5 169.4 256 36.9 388.5l22.6 22.6L192 278.6 324.5 411.1l22.6-22.6L214.6 256 347.1 123.5l-22.6-22.6L192 233.4z"
                                />
                            </svg>
                        </button>
                        <fp-frame
                            fanPassTitle=${this.fanPassTitle}
                            maxPages=${3}
                            currentPageNumber=${this._pageNumber}
                            .logoSet=${this.logoSet}
                            logoUrl=${this.logoUrl}
                            exportparts="${frameShadowParts}"
                        >
                            ${this.renderContent()}
                        </fp-frame>
                    </pu-box>
                    
                    
                </pu-overlay>
            `
        }
        return html`
            ${this.walletCheckResponse && this.loginController?.loggedIn
                ? html`
                      <fp-connected
                          displayText=${this.walletCheckResponse.data?.holds
                              ? 'You already have this ${this.fanPassTitle}'
                              : this.claimIntroText.replace('${fanPassTitle}', this.fanPassTitle) }
                          fanPassTitle=${this.fanPassTitle}
                          apiKey=${this.apiKey}
                          environment=${this.environment}
                          ?debugLogging=${this.debugLogging}
                          ?ownsFp=${this.walletCheckResponse.data?.holds
                              ? true
                              : false}
                          exportparts="${connectedShadowParts}"
                        >
                          <slot></slot>
                      </fp-connected>
                  `
                : html``}
            <fp-frame
                fanPassTitle=${this.fanPassTitle}
                maxPages=${3}
                currentPageNumber=${this._pageNumber}
                .logoSet=${this.logoSet}
                logoUrl=${this.logoUrl}
                exportparts="${frameShadowParts}"
            >
                ${this.renderContent()}
            </fp-frame>
        `
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fan-pass-claim': FanPassClaim
    }
}
